import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CompanySettings {
  name: string | null;
  industry: string | null;
  team_size: string | null;
  monthly_budget: string | null;
  saved_card_last4: string | null;
  saved_card_brand: string | null;
  saved_card_exp_month: number | null;
  saved_card_exp_year: number | null;
  autopay_enabled: boolean | null;
  auto_approve_bakery: string | null;
  /** New strategic flag (2026-05-13), opts the company into concierge
   *  autopilot, where qualified employees auto-receive intentional gifts
   *  routed by the engine v2 (per-recipient, no single-bakery default).
   *  Distinct from the legacy `autopay_enabled` (single-bakery autopay)
   *  which the Sprint 1.3 work leaves in place but no longer surfaces
   *  prominently. */
  concierge_autopilot_enabled: boolean | null;
  /** MS79: company-level fallback tier for the recommendation engine.
   *  When set, Dashboard's recommendationFor uses this in place of the
   *  hardcoded "standard". Per-recipient team_members.tier_override
   *  still wins over this when present. */
  default_tier: string | null;
  /** MS556: pool cadence governs whether the dashboard pool-status
   *  widget renders. `per_celebration` (or null/unconfigured) hides the
   *  widget entirely — there's no shared pool to track. `weekly` /
   *  `monthly` / `quarterly` enables the widget against the active
   *  company_budget_periods row. MS554's Settings → Budget panel writes
   *  this column; until that lands, new companies see null and the
   *  widget stays hidden. Schema landed in MS553. */
  pool_cadence: "per_celebration" | "weekly" | "monthly" | "quarterly" | null;
  /** MS554: default per-celebration ceiling (in cents) — the company-wide
   *  control-layer default surfaced everywhere a tier ceiling isn't set.
   *  Always editable from Settings regardless of cadence; when cadence
   *  is `per_celebration` it's the sole spending guardrail. Stored in
   *  cents so the column matches the rest of the budget schema (MS553).
   *  Null = unset; the recommendation engine falls back to the legacy
   *  bracket. */
  per_celebration_ceiling_cents: number | null;
  /** MS554: rollover policy — `carry_over` (default) or `reset`. Governs
   *  what happens to unspent pool dollars at period end. Only meaningful
   *  when `pool_cadence` is one of the pooled values. The Settings panel
   *  hides this control entirely when cadence is `per_celebration`
   *  because there's no pool to roll. Schema landed in MS553. */
  rollover_policy: "carry_over" | "reset" | null;
  /** MS554: staged cadence change. When a buyer moves between two
   *  pooled cadences (e.g. monthly → quarterly), we write the new value
   *  to `next_cadence` rather than mutating `pool_cadence` directly —
   *  the current period keeps its rhythm to avoid mid-period weirdness,
   *  and the rollover cron picks up `next_cadence` at the boundary
   *  (MS551 cadence semantics). Per_celebration ↔ pooled transitions
   *  write `pool_cadence` directly because there's no current pooled
   *  period to preserve. Schema landed in MS553. */
  next_cadence: "per_celebration" | "weekly" | "monthly" | "quarterly" | null;
}

export interface CompanyAddressRow {
  id: string;
  label: string;
  address: string;
  is_default: boolean | null;
}

/**
 * Settings-view company data for a single company. Includes saved-card
 * columns so Settings can render the payment section and the
 * auto-pay toggle without a separate query.
 */
export function useCompanySettings(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["companies", companyId, "settings"],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<CompanySettings | null> => {
      if (!companyId) return null;
      const { data, error } = await supabase
        .from("companies")
        .select(
          "name, industry, team_size, monthly_budget, saved_card_last4, saved_card_brand, saved_card_exp_month, saved_card_exp_year, autopay_enabled, auto_approve_bakery, concierge_autopilot_enabled, default_tier, pool_cadence, per_celebration_ceiling_cents, rollover_policy, next_cadence",
        )
        .eq("id", companyId)
        .single();
      if (error) throw error;
      // Generated supabase types don't yet include pool_cadence /
      // per_celebration_ceiling_cents / rollover_policy / next_cadence
      // (the types file is regenerated post-merge). The select() string
      // requests them from PostgREST and the columns exist in DB
      // (MS553); cast through unknown so the typed CompanySettings
      // reflects the runtime shape until types regenerate downstream
      // of MS554.
      return (data as unknown as CompanySettings | null) ?? null;
    },
  });
}

/**
 * Company addresses, ordered by created_at. Used by Settings and the
 * order-create flow for address pickers.
 */
export function useCompanyAddresses(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["company_addresses", companyId],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<CompanyAddressRow[]> => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("company_addresses")
        .select("id, label, address, is_default")
        .eq("company_id", companyId)
        .order("created_at");
      if (error) throw error;
      return (data as CompanyAddressRow[] | null) ?? [];
    },
  });
}

/**
 * Returns true when the company has a saved card on file. Used by the
 * post-onboarding checklist to decide whether "Save a payment method" is
 * still outstanding. We key on the presence of `saved_card_last4` because
 * that column is only populated once setup-payment-method finishes, so
 * it's the earliest signal that a valid card exists.
 */
export function useCompanyHasSavedCard(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["companies", companyId, "has-saved-card"],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<boolean> => {
      if (!companyId) return false;
      const { data, error } = await supabase
        .from("companies")
        .select("saved_card_last4")
        .eq("id", companyId)
        .single();
      if (error) throw error;
      const last4 = (data as { saved_card_last4: string | null } | null)?.saved_card_last4;
      return Boolean(last4);
    },
  });
}

/**
 * MS389: shared helper for "does this company have a payment method on
 * file?" — used by the Dashboard's autopilot-without-a-card warning
 * banner and (when MS388 lands) the Settings autopilot toggle guard.
 * Currently this delegates to useCompanyHasSavedCard since both signals
 * are derived from companies.saved_card_last4. Naming this for the
 * higher-level intent so callers don't have to know the column. If we
 * ever migrate off saved_card_last4 (e.g. directly to a
 * default_payment_method_id Stripe ref), only this helper needs the
 * swap. MS388 should rebase onto this and use the same helper rather
 * than re-deriving "has card" from useCompanySettings inline.
 */
export function useHasPaymentMethod(companyId: string | null | undefined) {
  return useCompanyHasSavedCard(companyId);
}
